import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Card } from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useDispatch, useSelector } from "react-redux";
import { IconButton, Tooltip } from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import { toast } from "react-toastify";
import AddAddOnServiceModal from "./addAddOnServiceModal";
import {
  getAddOnMaster,
  deleteAddOnMaster,
} from "../../../store/slices/clientsSlice";

const ManageAddOnServicesTable = () => {
  const dispatch = useDispatch();
  const masterAddOns = useSelector((state) => state.clients?.masterAddOns);
  const [editItem, setEditItem] = useState({});
  const [isModalOpen, setModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchData = () => {
    dispatch(getAddOnMaster({ isMaster: true }));
  };

  useEffect(() => {
    fetchData();
  }, [dispatch]);

  const handleAdd = () => {
    setEditItem({});
    setModalOpen(true);
  };

  const handleEditClick = (e, row) => {
    e.stopPropagation();
    setEditItem(row.original);
    setModalOpen(true);
  };

  const handleDeleteClick = async (e, row) => {
    e.stopPropagation();
    const confirmed = window.confirm(
      `Are you sure you want to delete "${row.original.serviceName}"? This cannot be undone.`,
    );
    if (!confirmed) return;
    try {
      const result = await dispatch(deleteAddOnMaster({ id: row.original.ID }));
      if (result?.payload) {
        toast.success("Add-on service deleted successfully!");
        fetchData();
      } else {
        toast.error("Failed to delete add-on service.");
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred while deleting.");
    }
  };

  const toggleModal = () => {
    setModalOpen(!isModalOpen);
    if (isModalOpen) {
      setEditItem({});
    }
    fetchData();
  };

  const columns = useMemo(
    () => [
      { accessorKey: "ID", header: "ID", size: 80 },
      {
        accessorKey: "serviceName",
        header: "Service Name",
        size: 200,
        Cell: ({ row, renderedCellValue }) => (
          <span
            className="text-primary fw-bold"
            style={{ cursor: "pointer", textDecoration: "underline" }}
            onClick={(e) => handleEditClick(e, row)}
          >
            {renderedCellValue}
          </span>
        ),
      },
      { accessorKey: "serviceKey", header: "Service Key", size: 150 },
      { accessorKey: "maxSlot", header: "Max Slot", size: 100 },
      { accessorKey: "defaultFree", header: "Default Free", size: 110 },
      { accessorKey: "extraFree", header: "Extra Free", size: 100 },
      { accessorKey: "purchaseSLot", header: "Purchase Slot", size: 110 },
      {
        accessorKey: "isEnable",
        header: "Enabled",
        size: 100,
        Cell: ({ cell }) => (cell.getValue() ? "Yes" : "No"),
      },
      {
        id: "actions",
        header: "Actions",
        size: 100,
        Cell: ({ row }) => (
          <div style={{ display: "flex", gap: 4 }}>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => handleEditClick(e, row)}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={(e) => handleDeleteClick(e, row)}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        ),
      },
    ],
    [],
  );

  const table = useMaterialReactTable({
    columns,
    data: Array.isArray(masterAddOns) ? masterAddOns : [],
    initialState: {
      density: "compact",
      pagination: { pageSize: 10, pageIndex: 0 },
    },
    globalFilter: searchQuery,
    onGlobalFilterChange: setSearchQuery,
    state: { globalFilter: searchQuery },
    renderTopToolbarCustomActions: () => (
      <div className="d-flex">
        <button className="btn bg-primary" onClick={handleAdd}>
          Add Add-on Service
        </button>
      </div>
    ),
  });

  return (
    <Fragment>
      <AddAddOnServiceModal
        isOpen={isModalOpen}
        toggle={toggleModal}
        editItem={editItem}
      />
      <Breadcrumbs
        mainTitle="Manage Add-on Services"
        parent="Program Module"
        parentPath="manage-addon-services"
        title="Add-on Services List"
      />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <MaterialReactTable table={table} />
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default ManageAddOnServicesTable;
